import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, MoreHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePayments } from '@/composables/financial/usePayments';
import { useAccounts } from '@/composables/financial/useAccounts';
import { useDebounce } from '@/composables/shared/useDebounce';
import { paymentSchema, type PaymentFormData } from '@/lib/validators';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { formatDate, formatCurrency } from '@/lib/formatters';
import type { Payment } from '@/types/table';

const methodOptions = [
  { label: 'Cash', value: 'CASH' },
  { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
  { label: 'Credit Card', value: 'CREDIT_CARD' },
  { label: 'Debit Card', value: 'DEBIT_CARD' },
  { label: 'Check', value: 'CHECK' },
  { label: 'Online', value: 'ONLINE' },
  { label: 'Other', value: 'OTHER' },
];

const currencyOptions = [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'INR', value: 'INR' },
  { label: 'CAD', value: 'CAD' },
  { label: 'AUD', value: 'AUD' },
];

type EditPaymentFormData = Pick<PaymentFormData, 'paymentMethod' | 'paymentDate' | 'referenceNumber' | 'notes'>;

export function PaymentListPage() {
  const { payments, meta, isLoading, filters, createPayment, updatePayment, deletePayment } = usePayments();
  const { accounts } = useAccounts();
  const [search, setSearch] = useState(filters.current.search);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Payment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Payment | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const form = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: { amount: 0, currency: 'USD', paymentMethod: 'BANK_TRANSFER', referenceNumber: '', paymentDate: '', notes: '', invoiceId: '', accountId: '' },
  });

  const editForm = useForm<EditPaymentFormData>({
    defaultValues: { paymentMethod: 'BANK_TRANSFER', paymentDate: '', referenceNumber: '', notes: '' },
  });

  const handleCreate = (data: PaymentFormData) => {
    createPayment.mutate(data, { onSuccess: () => { setShowCreate(false); form.reset(); } });
  };

  const openEdit = (p: Payment) => {
    editForm.reset({
      paymentMethod: p.paymentMethod as PaymentFormData['paymentMethod'],
      paymentDate: (p.paymentDate || '').slice(0, 10),
      referenceNumber: p.referenceNumber || '',
      notes: p.notes || '',
    });
    setEditTarget(p);
  };

  const handleEdit = (data: EditPaymentFormData) => {
    if (!editTarget) return;
    updatePayment.mutate({ id: editTarget.id, data }, { onSuccess: () => setEditTarget(null) });
  };

  const columns: ColumnDef<Payment, unknown>[] = useMemo(() => [
    { accessorKey: 'paymentDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />, cell: ({ row }) => formatDate(row.original.paymentDate) },
    { accessorKey: 'amount', header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />, cell: ({ row }) => <span className="font-medium">{formatCurrency(row.original.amount, row.original.currency)}</span> },
    { accessorKey: 'paymentMethod', header: 'Method', cell: ({ row }) => row.original.paymentMethod.replace('_', ' ') },
    { accessorKey: 'referenceNumber', header: 'Reference', cell: ({ row }) => row.original.referenceNumber || '-' },
    { accessorKey: 'invoice', header: 'Invoice', cell: ({ row }) => row.original.invoice?.invoiceNumber || '-' },
    { accessorKey: 'account', header: 'Account', cell: ({ row }) => row.original.account?.name || '-' },
    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) => (
        <span className="text-muted-foreground whitespace-pre-wrap line-clamp-2 max-w-xs block" title={row.original.notes || ''}>
          {row.original.notes || '-'}
        </span>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>Edit</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Payments" description="Track all payments" actions={<Button onClick={() => setShowCreate(true)}><Plus className="mr-2 h-4 w-4" />Record Payment</Button>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search payments..." filters={
        <Select value={filters.current.accountId || 'all'} onValueChange={(v) => filters.set({ accountId: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All Accounts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Accounts</SelectItem>
            {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      } />
      <DataTable columns={columns} data={payments} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} />
      <FormDialog open={showCreate} onOpenChange={setShowCreate} title="Record Payment" description="Record a new payment" onSubmit={form.handleSubmit(handleCreate)} isSubmitting={createPayment.isPending}>
        <FormCurrencyInput form={form} name="amount" label="Amount" />
        <FormSelect form={form} name="currency" label="Currency" options={currencyOptions} />
        <FormSelect form={form} name="paymentMethod" label="Payment Method" options={methodOptions} required />
        <FormDatePicker form={form} name="paymentDate" label="Payment Date" />
        <FormField form={form} name="referenceNumber" label="Reference Number" />
        <FormTextarea form={form} name="notes" label="Notes" />
        <FormSelect form={form} name="accountId" label="To Account" options={accounts.map(a => ({ label: `${a.name} (${a.type.replace('_', ' ')})`, value: a.id }))} />
      </FormDialog>
      <FormDialog
        open={!!editTarget}
        onOpenChange={(o) => { if (!o) setEditTarget(null); }}
        title="Edit Payment"
        description={editTarget ? `Editing payment of ${formatCurrency(editTarget.amount, editTarget.currency)}. Amount and linked account/invoice cannot be changed here — delete and re-record if needed.` : ''}
        onSubmit={editForm.handleSubmit(handleEdit)}
        isSubmitting={updatePayment.isPending}
        submitLabel="Save"
      >
        <FormSelect form={editForm} name="paymentMethod" label="Payment Method" options={methodOptions} required />
        <FormDatePicker form={editForm} name="paymentDate" label="Payment Date" />
        <FormField form={editForm} name="referenceNumber" label="Reference Number" />
        <FormTextarea form={editForm} name="notes" label="Notes" />
      </FormDialog>
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Payment"
        description={`Delete this payment of ${deleteTarget ? formatCurrency(deleteTarget.amount, deleteTarget.currency) : ''}?${deleteTarget?.invoice ? ` The linked invoice (${deleteTarget.invoice.invoiceNumber}) will be marked as unpaid.` : ''} This will also reverse the account balance.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deletePayment.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deletePayment.isPending}
      />
    </PageContainer>
  );
}
